import {
  integer,
  pgEnum,
  pgTable,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import { usStatesEnum, usStatesEnumSchema } from "./commonSchema.ts";
import { customJsonb, customTimestamp } from "./utils/types.ts";

export const consentDocumentTypeEnumSchema = z.enum([
  "AR_MEDICAID",
  "AR_SNAP",
  "TCPA",
  "AR_MEDICAID_ADDITIONAL",
  "BROKER_OF_RECORD",
  "IDENTITY_VERIFICATION",
  "HEALTH_PLAN_SELECTION_ENROLLMENT",
  "AR_HEALTH_PLAN_SELECTION",
  "HEALTH_PLAN_CHANGE",
]);
export type ConsentDocumentTypeEnumType = z.infer<
  typeof consentDocumentTypeEnumSchema
>;
export const consentDocumentTypeEnum = pgEnum(
  "consent_document_type_enum",
  consentDocumentTypeEnumSchema.enum,
);

const boxCoordinateSchema = z.object({
  page: z.number().int().positive(),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

export const signatureBoxCoordinatesSchema = boxCoordinateSchema.array();
export type SignatureBoxCoordinatesType = z.infer<
  typeof signatureBoxCoordinatesSchema
>;

export const pcNameFormatEnumSchema = z.enum([
  "full",
  "first",
  "last",
  "middle",
  "lastFirstInitial",
]);
export type PcNameFormatEnumType = z.infer<typeof pcNameFormatEnumSchema>;

export const pcNameCoordinatesSchema = boxCoordinateSchema
  .extend({
    fontSize: z.number(),
    format: pcNameFormatEnumSchema,
  })
  .array();
export type PcNameCoordinatesType = z.infer<typeof pcNameCoordinatesSchema>;

export const initialsCoordinatesSchema = boxCoordinateSchema
  .extend({
    fontSize: z.number(),
  })
  .array();
export type InitialsCoordinatesType = z.infer<typeof initialsCoordinatesSchema>;

export const consentDocumentsTable = pgTable(
  "consent_documents",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    type: consentDocumentTypeEnum("type").notNull(),
    usState: usStatesEnum("us_state").notNull(),
    versionNumber: integer("version_number").notNull(),
    language: text("language").notNull(),
    storageKey: text("storage_key").notNull(),
    signatureBoxCoordinates: customJsonb<SignatureBoxCoordinatesType>(
      "signature_box_coordinates",
    ),
    pcSignatureBoxCoordinates: customJsonb<SignatureBoxCoordinatesType>(
      "pc_signature_box_coordinates",
    ),
    pcNameCoordinates: customJsonb<PcNameCoordinatesType>(
      "pc_name_coordinates",
    ),
    patientInitialsCoordinates: customJsonb<InitialsCoordinatesType>(
      "patient_initials_coordinates",
    ),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    unique("consent_docs_type_state_lang_version_unique")
      .on(table.type, table.usState, table.language, table.versionNumber)
      .nullsNotDistinct(),
  ],
);

export const consentDocumentsInsertSchema = createInsertSchema(
  consentDocumentsTable,
  {
    type: consentDocumentTypeEnumSchema,
    usState: usStatesEnumSchema,
    signatureBoxCoordinates: signatureBoxCoordinatesSchema.nullish(),
    pcSignatureBoxCoordinates: signatureBoxCoordinatesSchema.nullish(),
    pcNameCoordinates: pcNameCoordinatesSchema.nullish(),
    patientInitialsCoordinates: initialsCoordinatesSchema.nullish(),
  },
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const consentDocumentsSchema = createSelectSchema(
  consentDocumentsTable,
  {
    type: consentDocumentTypeEnumSchema,
    usState: usStatesEnumSchema,
    signatureBoxCoordinates: signatureBoxCoordinatesSchema.nullish(),
    pcSignatureBoxCoordinates: signatureBoxCoordinatesSchema.nullish(),
    pcNameCoordinates: pcNameCoordinatesSchema.nullish(),
    patientInitialsCoordinates: initialsCoordinatesSchema.nullish(),
  },
);

export type ConsentDocumentType = z.infer<typeof consentDocumentsSchema>;

export type ConsentDocumentInsertType = z.infer<
  typeof consentDocumentsInsertSchema
>;
